/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { sortBy } from 'lodash'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Switch from '@mui/material/Switch'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { SubjectExtended } from 'hooks/usePlatformsAndSubjects'
import Table from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { ClassroomTabLiveSwitcher } from 'components/ClassroomTabLiveSwitcher'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { PageLoader } from 'components/PageLoader'
import { toast } from 'utils/toast'
import { getQBTitle, isCaseScenarioSubject, getQBClassroomTab } from '../utils'
import * as classes from './styles'
import { useCategoriesApi, QuestionBank, QuestionBankCategory } from './useCategoriesApi'

export type ListWithoutCategoriesProps = {
  selectedSubject: SubjectExtended
}

export const ListWithoutCategories = ({ selectedSubject }: ListWithoutCategoriesProps) => {
  const { navigate, getLink } = useLink()
  const [categories, setCategories] = useState<QuestionBankCategory[]>([])
  const [questionBanks, setQuestionBanks] = useState<QuestionBank[]>([])
  const [submittingQuestionBankId, setSubmittingQuestionBankId] = useState<string | undefined>()
  const [isLoaded, setIsLoaded] = useState(false)
  const categoriesApi = useCategoriesApi()
  const isCaseBasedScenario = isCaseScenarioSubject(selectedSubject)

  const loadCategories = useCallback(async () => {
    if (!selectedSubject?.id) {
      return
    }

    try {
      const categories = await categoriesApi.getCategories(selectedSubject)
      setCategories(categories)
      setIsLoaded(true)
    } catch (e) {
      toast.error('Failed to load categories')
    }
  }, [selectedSubject.id, selectedSubject.tabIndex])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  useEffect(() => {
    if (!categories.length) {
      setQuestionBanks([])
      return
    }

    const questionBanks = categories.reduce(
      (questionBanks, categoty) => [...questionBanks, ...categoty.questionBanks],
      [] as QuestionBank[]
    )

    setQuestionBanks(sortBy(questionBanks, 'order'))
  }, [categories])

  const handleAddQuestionBankClick = useCallback(() => {
    if (!selectedSubject) {
      return
    }

    navigate(cmsRoutes.QUESTION_BANK_CREATE, undefined, {
      searchParams: new URLSearchParams({
        subjectId: selectedSubject.id, // todo
        defaultOrder: String(questionBanks.length),
        tabIndex: String(selectedSubject.tabIndex),
      }),
    })
  }, [selectedSubject, questionBanks.length])

  const handleSortQuestionBanks = useCallback(
    async (questionBanksUpd: QuestionBank[]) => {
      if (!selectedSubject) {
        return
      }

      setQuestionBanks(questionBanksUpd)

      try {
        await categoriesApi.updateQBsOrder(selectedSubject, questionBanksUpd)
        toast.success(`${getQBTitle(isCaseBasedScenario, selectedSubject.tabIndex, true)} order updated successfully`)
      } catch (e) {
        toast.error(
          `Failed to update ${getQBTitle(
            isCaseBasedScenario,
            selectedSubject.tabIndex,
            true
          ).toLocaleLowerCase()} order`
        )
        await loadCategories()
      }
    },
    [selectedSubject, loadCategories]
  )

  const handleIsLiveQuestionBankChange = useCallback(
    async (questionBank: QuestionBank, isLiveUpd: boolean) => {
      if (!selectedSubject) {
        return
      }

      setSubmittingQuestionBankId(questionBank.id)

      setCategories((prev) =>
        prev.map((currentCategory) => {
          const questionBanks = currentCategory.questionBanks.map((currentQuestionBank) => {
            if (questionBank.id !== currentQuestionBank.id) {
              return currentQuestionBank
            }

            return {
              ...currentQuestionBank,
              isLive: isLiveUpd,
            }
          })

          return {
            ...currentCategory,
            questionBanks,
          }
        })
      )

      try {
        await categoriesApi.updateQBIsLive(selectedSubject, questionBank.id, isLiveUpd)
        toast.success(`${getQBTitle(isCaseBasedScenario, selectedSubject.tabIndex)} updated successfully`)
      } catch (e) {
        toast.error(`Failed to update ${getQBTitle(isCaseBasedScenario, selectedSubject.tabIndex).toLocaleLowerCase()}`)
        await loadCategories()
      }

      setSubmittingQuestionBankId(undefined)
    },
    [selectedSubject, loadCategories]
  )

  const getEditLink = useCallback(
    (questionBankId: string) => {
      if (!selectedSubject) {
        return ''
      }

      const searchParams = new URLSearchParams({
        tabIndex: String(selectedSubject.tabIndex),
      })

      return getLink(
        cmsRoutes.QUESTION_BANK_EDIT,
        {
          questionBankId,
        },
        searchParams
      )
    },
    [selectedSubject]
  )

  if (!isLoaded) {
    return <PageLoader />
  }

  const classroomTabEnum = getQBClassroomTab(selectedSubject)

  return (
    <div css={classes.tablesWrapper}>
      <div css={classes.categoriesWrapper}>
        <div css={classes.tableHeader}>
          <Typography variant="body1" css={classes.tableTitle}>
            {getQBTitle(isCaseBasedScenario, selectedSubject.tabIndex, true)}
          </Typography>
          <ClassroomTabLiveSwitcher selectedSubject={selectedSubject} tabEnum={classroomTabEnum} />
        </div>
        <Table<QuestionBank>
          css={classes.categoriesTable}
          columns={[
            {
              title: 'Title',
              renderCell: (questionBank) => questionBank.title,
            },
            {
              title: (
                <div css={classes.countWrapper}>
                  <div css={classes.countTitle}># of Questions</div>
                </div>
              ),
              renderCell: (questionBank) => <div css={classes.countValue}>{questionBank.questionIds.length}</div>,
              flex: '0 1 50px',
            },
            {
              title: 'Live',
              renderCell: (questionBank) => (
                <Switch
                  checked={questionBank.isLive}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e, isLive) => handleIsLiveQuestionBankChange(questionBank, isLive)}
                  disabled={questionBank.id === submittingQuestionBankId}
                />
              ),
              flex: '0 1 50px',
            },
            {
              renderCell: (questionBank) => (
                <Link to={getEditLink(questionBank.id)}>
                  <IconButton size="small" css={classes.link}>
                    <EditIcon />
                  </IconButton>
                </Link>
              ),
              flex: '0 1 30px',
            },
          ]}
          items={questionBanks}
          onSort={handleSortQuestionBanks}
          isSortable
        />
        <TableButtons
          itemName={getQBTitle(isCaseBasedScenario, selectedSubject.tabIndex, false)}
          onAddItem={handleAddQuestionBankClick}
          disabled={!selectedSubject}
        />
      </div>
    </div>
  )
}
